import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F14] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#25D366]/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#10B981]/10 rounded-full blur-3xl" />
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#25D366] flex items-center justify-center">
            <MessageCircle className="w-7 h-7 text-[#0B0F14]" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-slate-50 leading-none">LeadFlow.WA</h1>
            <p className="text-xs font-mono-lf uppercase tracking-widest text-[#25D366] mt-1">Envolvía Global</p>
          </div>
        </div>

        <div className="bg-[#111822] border border-[#1E2D3D] rounded-2xl p-8 shadow-2xl">
          <h2 className="font-heading text-xl font-bold text-slate-100 mb-1">Iniciar sesión</h2>
          <p className="text-sm text-slate-400 mb-6">Panel de reparto de leads</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Correo</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100 h-11"
                required
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Contraseña</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100 h-11"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#25D366] hover:bg-[#22BF5B] text-[#0B0F14] font-semibold text-base"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm text-slate-400 hover:text-[#25D366] transition-colors">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
        </div>
        <p className="text-center text-xs text-slate-600 mt-6 font-mono-lf">Solo el administrador crea cuentas nuevas</p>
      </div>
    </div>
  );
}
